import { h } from '@omni-torn/ui';
import {
  compareFactions,
  estimateDistribution,
  estimateTrustWord,
  findFactions,
  parseFactionQuery,
  warsBetween,
  type EstimateDistribution,
  type FactionCompareRow,
  type FactionMemberEstimate,
  type FactionProfileAnswer,
  type FactionRosterMember,
  type RankedWarInfo,
} from '@omni-torn/shared';
import { FACTION_HOF_CATEGORIES } from '@omni-torn/shared/torn-ref';
import { navigateTo } from '../nav';
import { server } from '../session';
import { ago, box, duration, ext, facts, kids, loading, num, problem, profileUrl, short, table } from '../ui';
import { ESTIMATE_NOTE, methodWord } from './estimate-view';
import { appendFactionLists, ref } from './torn';
import { compareActivityBox, factionActivityBox } from './activity-view';
import { factionsNav, renderScout, type ScoutPage } from './factions-scout';

/**
 * Factions (website roadmap S14 — FF Scouter's Faction Profile and Compare Factions, Torn Intel's comparison and
 * battle-stat distribution; it began as "Factions & wars", whose lists stay at the bottom of /factions):
 *  - find a faction by id, Torn link, or name (names among the factions Omni-Torn has lists of: the Hall of Fame tops and
 *    the ranked wars);
 *  - /factions/{id}: its profile (respect, rank, members, age, leaders, best chain, the chain now), its ranked wars, and its
 *    roster with Omni-Torn's estimate of each member and how they spread (average, median, the strongest, buckets);
 *  - /factions/compare/{a}/{b}: two side by side, with their wars against each other;
 *  - /factions/watch, /factions/recruit, /factions/transfers: the watch list, recruitment and transfers (S24, factions-scout.ts).
 * Data: GET /api/factions/:id — Torn's public faction data read by the Omni-Torn server on demand (lent keys or its own
 * key, kept an hour there) with the player database's estimates. Nothing is kept by this page or sent anywhere.
 */

export async function renderFactions(view: HTMLElement, path: string): Promise<void> {
  const cmp = /^\/factions\/compare\/(\d+)\/(\d+)\/?$/.exec(path);
  const one = /^\/factions\/(\d+)\/?$/.exec(path)?.[1];
  const sub = /^\/factions\/(watch|recruit|transfers)\/?$/.exec(path)?.[1] as ScoutPage | undefined;
  const holder = h('div', {});
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Factions'), h('p', { class: 'muted' }, 'Any faction’s profile, roster and Omni-Torn’s estimates of its members — or two side by side.')),
    factionsNav(sub ?? 'find'),
    ...(sub ? [] : [searchBar()]),
    holder,
  );
  if (sub) return renderScout(holder, sub);
  if (cmp) return renderCompare(holder, Number(cmp[1]), Number(cmp[2]));
  if (one) return renderProfile(holder, Number(one));
  document.title = 'Factions · Omni-Torn';
  await appendFactionLists(holder, () => renderFactions(view, path));
}

// ---------------------------------------------------------------- finding a faction

/** Factions Omni-Torn has lists of (Hall of Fame tops, ranked wars) — the server keeps them, so this costs no extra Torn call beyond those kept public lists. */
let known: Promise<Array<{ id: number; name: string }>> | null = null;
function knownFactions(): Promise<Array<{ id: number; name: string }>> {
  known ??= Promise.all([
    ...FACTION_HOF_CATEGORIES.map((c) => ref('factionhof', c.id).then((a) => a.data.map((r) => ({ id: r.id, name: r.name }))).catch(() => [])),
    ref('rankedwars').then((a) => a.data.flatMap((w) => w.factions.map((f) => ({ id: f.id, name: f.name })))).catch(() => []),
  ]).then((lists) => lists.flat());
  return known;
}

/** A faction id from what was typed: an id or link at once, else a name found in the known lists (exactly one, or the exact name). */
async function factionIdOf(raw: string): Promise<{ id: number } | { matches: Array<{ id: number; name: string }> } | null> {
  const q = parseFactionQuery(raw);
  if (!q) return null;
  if ('id' in q) return q;
  const matches = findFactions(await knownFactions(), q.name);
  const exact = matches.filter((m) => m.name.toLowerCase() === q.name);
  if (exact.length === 1) return { id: exact[0]!.id };
  if (matches.length === 1) return { id: matches[0]!.id };
  return { matches };
}

function searchBar(): HTMLElement {
  const input = h('input', { id: 'faction-search', type: 'search', placeholder: 'Faction name, id or Torn link…', maxlength: '120', autocomplete: 'off', 'aria-label': 'Faction name, id or Torn link' }) as HTMLInputElement;
  const found = h('ul', { class: 'suggestions', 'data-faction': 'found', hidden: true }) as HTMLUListElement;
  const form = h('form', { class: 'search', role: 'search', autocomplete: 'off', 'data-form': 'faction-search' }, input, h('button', { type: 'submit' }, 'Look up'), found);
  const list = (items: Array<{ id: number; name: string }>, note: string) => {
    found.replaceChildren(...items.map((m) => h('li', {}, h('a', { href: `/factions/${m.id}`, 'data-link': true, onclick: () => (found.hidden = true) }, m.name, h('span', { class: 'muted' }, ` [${m.id}]`)))), h('li', { class: 'muted empty' }, note));
    found.hidden = false;
  };
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const r = await factionIdOf(input.value);
    if (!r) return list([], 'Type a faction’s name (2 letters or more), its id, or paste its Torn link.');
    if ('id' in r) {
      found.hidden = true;
      return navigateTo(`/factions/${r.id}`);
    }
    list(r.matches, r.matches.length ? 'Pick one.' : 'No faction by that name in Omni-Torn’s lists (the top factions and the ranked wars) — type its id or paste its Torn link instead.');
  });
  input.addEventListener('keydown', (e) => e.key === 'Escape' && (found.hidden = true));
  return h('div', { class: 'lookup-bar' }, form);
}

// ---------------------------------------------------------------- reading

async function readFaction(id: number): Promise<FactionProfileAnswer> {
  return server<FactionProfileAnswer>('GET', `/api/factions/${id}`);
}

const estimatesOf = (a: FactionProfileAnswer): Map<number, FactionMemberEstimate> => new Map(a.estimates.map((e) => [e.id, e]));
const distOf = (a: FactionProfileAnswer): EstimateDistribution => estimateDistribution(a.members.map((m) => m.id), a.estimates);

/** "From Torn's API, 12m ago" and what didn't come. */
function fromTorn(a: FactionProfileAnswer): HTMLElement {
  return h('p', { class: 'muted small ref-note', 'data-faction': 'note' }, `From Torn’s API, ${ago(a.at)}${a.calls ? '' : ' (kept by Omni-Torn for an hour)'}.`, a.problem ? ` ${a.problem}` : '');
}

// ---------------------------------------------------------------- one faction

async function renderProfile(holder: HTMLElement, id: number): Promise<void> {
  document.title = `Faction ${id} · Factions · Omni-Torn`;
  holder.replaceChildren(loading('Reading the faction…'));
  let a: FactionProfileAnswer;
  try {
    a = await readFaction(id);
  } catch (err) {
    holder.replaceChildren(problem(err), h('p', {}, h('a', { href: '/factions', 'data-link': true }, '← All factions')));
    return;
  }
  document.title = `${a.info.name} · Factions · Omni-Torn`;
  const est = estimatesOf(a);
  const dist = distOf(a);
  // When its members play (website roadmap S22): what Omni-Torn kept — no Torn call.
  const activity = h('div', {});
  holder.replaceChildren(
    infoBox(a),
    h('div', { class: 'cards two' }, distBox(dist), warsBox(a.id, a.wars)),
    activity,
    rosterBox(a.members, est),
    compareBox(a.id),
  );
  void factionActivityBox(a.id).then((b) => activity.replaceChildren(b));
}

/** Seconds before the chain runs out without a hit (0: not known or already past). */
const chainLeft = (a: FactionProfileAnswer): number => (a.chain?.timeout ? Math.max(0, Math.round((a.at + a.chain.timeout * 1000 - Date.now()) / 1000)) : 0);

function infoBox(a: FactionProfileAnswer): HTMLElement {
  const f = a.info;
  const rank = f.rank ? `${f.rank.name}${f.rank.division ? ` ${'I'.repeat(Math.min(3, f.rank.division))}` : ''}${f.rank.position ? ` · #${num(f.rank.position)}` : ''}` : '—';
  const person = (pid: number | null) => (pid ? h('span', {}, h('a', { href: `/lookup/${pid}`, 'data-link': true }, a.members.find((m) => m.id === pid)?.name ?? `[${pid}]`)) : null);
  const online = a.members.filter((m) => m.activity === 'online').length;
  return box(
    h('div', { class: 'spy-head' }, h('h2', {}, `${f.name}${f.tag ? ` [${f.tag}]` : ''}`), ext(`https://www.torn.com/factions.php?step=profile&ID=${f.id}`, 'Open in Torn ↗'), h('a', { href: `/loadouts?faction=${f.id}`, 'data-link': true, 'data-loadouts-link': '' }, 'Loadouts seen'), h('a', { href: `/factions/transfers?faction=${f.id}`, 'data-link': true, 'data-transfers-link': '' }, 'Transfers')),
    [
      facts([
        ['Respect', num(f.respect)],
        ['Rank', rank],
        ['Ranked war wins', num(f.rank?.wins)],
        ['Members', `${num(a.members.length || f.memberCount)}${f.capacity ? ` of ${num(f.capacity)}` : ''} · ${num(online)} online`],
        ['Age', f.daysOld !== null ? `${num(f.daysOld)} days` : '—'],
        ['Leader', person(f.leaderId)],
        ['Co-leader', person(f.coLeaderId)],
        ['Best chain', num(f.bestChain)],
        ['Chain now', a.chain ? `${num(a.chain.current)}${a.chain.max ? ` of ${num(a.chain.max)}` : ''}${chainLeft(a) ? ` · runs out in ${duration(chainLeft(a))} unless they hit` : ''}` : 'none'],
      ]),
      fromTorn(a),
    ],
    { id: 'faction-info' },
  );
}

/** The estimates' spread: tiles (known, average, median, five strongest, strongest) and the buckets. */
function distTiles(d: EstimateDistribution): HTMLElement {
  const tile = (label: string, v: number | null, note = '') => h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value', title: num(v) }, short(v)), note ? h('div', { class: 'muted' }, note) : null);
  return h(
    'div',
    { class: 'tiles' },
    h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Estimated'), h('div', { class: 'tile-value' }, `${d.known} of ${d.members}`), h('div', { class: 'muted' }, 'members')),
    tile('Average', d.average),
    tile('Median', d.median),
    tile('Five strongest', d.topFive, 'average'),
    tile('Strongest', d.top),
  );
}

function bucketBars(d: EstimateDistribution, other?: EstimateDistribution): HTMLElement {
  const top = Math.max(1, ...d.buckets.map((b) => b.n), ...(other?.buckets.map((b) => b.n) ?? []));
  const bar = (n: number, cls = '') => {
    const fill = h('span', { class: `bl-fill ${cls}`.trim() });
    fill.style.width = `${(n / top) * 100}%`;
    return h('span', { class: 'bl-track' }, fill);
  };
  return h(
    'ul',
    { class: `bars-list ${other ? 'bars-pair' : ''}`.trim(), 'data-faction': 'buckets' },
    ...d.buckets.map((b, i) =>
      other
        ? h('li', {}, h('span', { class: 'bl-label' }, b.label), h('span', { class: 'bl-two' }, bar(b.n), bar(other.buckets[i]!.n, 'bl-fill-b')), h('span', { class: 'bl-n' }, `${b.n} · ${other.buckets[i]!.n}`))
        : h('li', {}, h('span', { class: 'bl-label' }, b.label), bar(b.n), h('span', { class: 'bl-n' }, num(b.n))),
    ),
  );
}

function distBox(d: EstimateDistribution): HTMLElement {
  return box(
    'Battle stats estimates',
    [
      distTiles(d),
      d.known ? bucketBars(d) : h('p', { class: 'muted' }, 'Omni-Torn has no estimates of its members yet.'),
      h('p', { class: 'muted small' }, 'Omni-Torn’s own estimates (estimated totals, not spies of yours). ', ESTIMATE_NOTE),
    ],
    { id: 'faction-dist' },
  );
}

function warLine(w: RankedWarInfo, self: number): HTMLElement {
  const us = w.factions.find((f) => f.id === self);
  const them = w.factions.find((f) => f.id !== self);
  const now = Date.now() / 1000;
  const result = w.end && w.end <= now ? (w.winner === self ? h('strong', { class: 't-green' }, 'won') : w.winner ? h('strong', { class: 't-red' }, 'lost') : h('span', { class: 'muted' }, 'ended')) : w.start > now ? h('span', { class: 'muted' }, 'starts soon') : h('strong', {}, 'going on');
  return h(
    'li',
    { class: 'war-row' },
    h('div', {}, result, ' vs ', them ? h('a', { href: `/factions/${them.id}`, 'data-link': true }, them.name) : '?', h('span', { class: 'muted' }, ` · ${num(us?.score)} to ${num(them?.score)}`)),
    h('div', { class: 'muted small' }, w.end && w.end <= now ? `ended ${ago(w.end * 1000)}` : `started ${ago(w.start * 1000)}`, ...(them ? [' · ', h('a', { href: `/factions/compare/${self}/${them.id}`, 'data-link': true }, 'compare')] : [])),
  );
}

function warsBox(self: number, wars: RankedWarInfo[] | null): HTMLElement {
  return box(
    'Ranked wars',
    wars === null ? h('p', { class: 'muted' }, 'Not read this time — open the faction again in a minute.') : wars.length ? h('ul', { class: 'plain-list wars' }, ...wars.map((w) => warLine(w, self))) : h('p', { class: 'muted' }, 'No ranked wars yet.'),
    { id: 'faction-wars' },
  );
}

const statusCell = (m: FactionRosterMember): HTMLElement => {
  if ((m.state === 'Hospital' || m.state === 'Jail') && m.until && m.until > Date.now()) return h('span', { class: 'st-red', title: m.description }, `${m.state === 'Jail' ? 'Jail' : 'Hospital'} · ${duration(Math.round((m.until - Date.now()) / 1000))}`);
  return h('span', { class: m.state === 'Okay' ? 't-green' : /travel|abroad/i.test(m.state) ? 'st-blue' : '' }, m.description || m.state);
};

function rosterBox(members: FactionRosterMember[], est: Map<number, FactionMemberEstimate>): HTMLElement {
  return box(
    `Members (${members.length})`,
    table(
      members,
      [
        { label: 'Player', get: (m) => h('span', {}, h('a', { href: `/lookup/${m.id}`, 'data-link': true }, m.name), h('span', { class: 'muted small' }, ` [${m.id}] `), ext(profileUrl(m.id), '↗')), sort: (m) => m.name.toLowerCase() },
        { label: 'Level', get: (m) => num(m.level), sort: (m) => m.level, cls: 'r' },
        { label: 'Position', get: (m) => m.position || '—', sort: (m) => m.position },
        { label: 'Days', get: (m) => num(m.daysInFaction), sort: (m) => m.daysInFaction ?? 0, cls: 'r' },
        {
          label: 'Estimate',
          get: (m) => {
            const e = est.get(m.id);
            return e ? h('span', { title: `${num(e.total)} — from ${methodWord(e.source)}${e.at ? `, ${ago(e.at)}` : ''}` }, short(e.total)) : h('span', { class: 'muted' }, '·');
          },
          sort: (m) => est.get(m.id)?.total ?? -1,
          cls: 'r',
        },
        { label: 'Trust', get: (m) => { const e = est.get(m.id); return e ? estimateTrustWord(e.source, e.at ? Date.now() - e.at : 0) : '—'; } },
        { label: 'Status', get: (m) => statusCell(m), sort: (m) => (m.state === 'Okay' ? 0 : 1) },
        { label: 'Last action', get: (m) => h('span', { class: `dot dot-${m.activity}`, title: m.activity }, m.lastAction ? ago(m.lastAction) : '—'), sort: (m) => m.lastAction ?? 0 },
      ],
      { initial: 4, empty: 'No members.' },
    ),
    { id: 'faction-roster' },
  );
}

function compareBox(id: number): HTMLElement {
  const input = h('input', { type: 'search', placeholder: 'Another faction: name, id or link', 'aria-label': 'Another faction', maxlength: '120' }) as HTMLInputElement;
  const status = h('span', { class: 'muted small', 'data-compare': 'status' });
  const form = h('form', { class: 'compare-form', 'data-form': 'faction-compare' }, input, h('button', { type: 'submit' }, 'Compare'), status);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const r = await factionIdOf(input.value);
    if (r && 'id' in r) return navigateTo(`/factions/compare/${id}/${r.id}`);
    status.textContent = r && r.matches.length ? `Which one? ${r.matches.map((m) => `${m.name} [${m.id}]`).join(', ')}` : 'Type its id or paste its Torn link (names: only factions in Omni-Torn’s lists).';
  });
  return box('Compare with another faction', [form, h('p', { class: 'muted small' }, 'Side by side: respect, rank, members, chains, how Omni-Torn’s estimates of their members spread, and when they play.')], { id: 'faction-compare-form' });
}

// ---------------------------------------------------------------- two factions

const cell = (r: FactionCompareRow, v: number | null): string => (v === null ? '—' : r.kind === 'stats' ? short(v) : r.kind === 'days' ? `${num(v)} days` : num(v));

async function renderCompare(holder: HTMLElement, idA: number, idB: number): Promise<void> {
  document.title = `Compare factions · Omni-Torn`;
  holder.replaceChildren(loading('Reading both factions…'));
  const [a, b] = await Promise.all([readFaction(idA).catch((e: Error) => e), readFaction(idB).catch((e: Error) => e)]);
  if (a instanceof Error || b instanceof Error) {
    holder.replaceChildren(...kids(a instanceof Error ? problem(a) : null, b instanceof Error ? problem(b) : null, h('p', {}, h('a', { href: '/factions', 'data-link': true }, '← All factions'))));
    return;
  }
  document.title = `${a.info.name} vs ${b.info.name} · Factions · Omni-Torn`;
  const da = distOf(a);
  const db = distOf(b);
  const rows = compareFactions({ info: a.info, members: a.members, dist: da, chain: a.chain }, { info: b.info, members: b.members, dist: db, chain: b.chain });
  const name = (x: FactionProfileAnswer) => h('a', { href: `/factions/${x.id}`, 'data-link': true }, x.info.name);
  const between = warsBetween(a, b);
  const activity = h('div', {});
  void compareActivityBox({ id: a.id, name: a.info.name }, { id: b.id, name: b.info.name }).then((x) => activity.replaceChildren(x));
  holder.replaceChildren(
    box(
      h('div', { class: 'spy-head' }, h('h2', {}, `${a.info.name} vs ${b.info.name}`), h('a', { href: `/factions/compare/${b.id}/${a.id}`, 'data-link': true, class: 'small' }, '⇄ swap')),
      [
        h(
          'div',
          { class: 'table-wrap' },
          h(
            'table',
            { 'data-testid': 'faction-compare-table' },
            h('thead', {}, h('tr', {}, h('th', { scope: 'col' }, ''), h('th', { scope: 'col', class: 'r' }, name(a)), h('th', { scope: 'col', class: 'r' }, name(b)))),
            h('tbody', {}, ...rows.map((r) => h('tr', {}, h('th', { scope: 'row' }, r.label), h('td', { class: `r ${r.ahead === 'a' ? 't-green' : ''}`.trim() }, cell(r, r.a)), h('td', { class: `r ${r.ahead === 'b' ? 't-green' : ''}`.trim() }, cell(r, r.b))))),
          ),
        ),
        h('p', { class: 'muted small' }, 'Green: ahead on that line. Estimates are Omni-Torn’s own (', ESTIMATE_NOTE.replace(/\.$/, ''), ').'),
        fromTorn(a),
        b.calls || b.problem ? fromTorn(b) : null,
      ],
      { id: 'faction-compare' },
    ),
    box(
      'How their estimates spread',
      [h('p', { class: 'small' }, h('span', { class: 'swatch' }), ` ${a.info.name}   `, h('span', { class: 'swatch swatch-b' }), ` ${b.info.name}`), da.known || db.known ? bucketBars(da, db) : h('p', { class: 'muted' }, 'Omni-Torn has no estimates of either faction’s members yet.')],
      { id: 'faction-compare-dist' },
    ),
    activity,
    box('Their ranked wars against each other', between.length ? h('ul', { class: 'plain-list wars' }, ...between.map((w) => warLine(w, a.id))) : h('p', { class: 'muted' }, a.wars && b.wars ? 'None in their last ranked wars.' : 'Their ranked wars weren’t read this time.'), { id: 'faction-compare-wars' }),
  );
}
